import { Button, Card, Empty, Input, InputNumber, notification, Popconfirm } from "antd";
import { get } from "lodash";
import { useState } from "react";
import { BiPlus, BiTrash } from "react-icons/bi";
import { useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import cuid from "cuid";

const DEFAULT_AGE_BRACKETS = [
  { name: "0-17", minAge: 0, maxAge: 17 },
  { name: "18-25", minAge: 18, maxAge: 25 },
  { name: "26-35", minAge: 26, maxAge: 35 },
  { name: "36-45", minAge: 36, maxAge: 45 },
  { name: "46-55", minAge: 46, maxAge: 55 },
  { name: "56-65", minAge: 56, maxAge: 65 },
  { name: "66+", minAge: 66, maxAge: 999 },
];

const AgeBracketManager = ({ ageBrackets, planId, setFieldValue }) => {
  const [adding, setAdding] = useState(false);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [createBracket] = useMutation(graphql.mutations.CREATE_AGE_BRACKET);
  const [updateBracket] = useMutation(graphql.mutations.UPDATE_AGE_BRACKET);
  const [deleteBracket] = useMutation(graphql.mutations.DELETE_AGE_BRACKET);

  const handleAddBracket = async () => {
    if (adding) return;
    setAdding(true);
    const id = cuid();
    const order = (ageBrackets || []).length + 1;
    const newBracket = { id, name: "", minAge: null, maxAge: null, order };

    try {
      await createBracket({
        variables: {
          data: { ...newBracket, insurancePlan: { id: planId } },
        },
      });
      setFieldValue("ageBrackets", [...(ageBrackets || []), newBracket]);
      notification.success({ message: "Tranche d'âge ajoutée" });
    } catch (err) {
      notification.error({ message: "Échec de l'ajout" });
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteBracket = async (index) => {
    const bracket = ageBrackets[index];
    try {
      await deleteBracket({ variables: { where: { id: bracket.id } } });
      setFieldValue(
        "ageBrackets",
        ageBrackets.filter((_, i) => i !== index),
      );
      notification.success({ message: "Tranche d'âge supprimée" });
    } catch (err) {
      notification.error({ message: "Échec de la suppression" });
    }
  };

  const handleLocalChange = (index, field, value) => {
    const updated = [...ageBrackets];
    updated[index] = { ...updated[index], [field]: value };
    setFieldValue("ageBrackets", updated);
  };

  const handleSaveField = async (index) => {
    const bracket = ageBrackets[index];
    try {
      await updateBracket({
        variables: {
          where: { id: bracket.id },
          data: {
            name: bracket.name,
            minAge: bracket.minAge,
            maxAge: bracket.maxAge,
            order: bracket.order,
          },
        },
      });
    } catch (err) {
      notification.error({ message: "Échec de la mise à jour" });
    }
  };

  const handleLoadDefaults = async () => {
    if (loadingDefaults) return;
    setLoadingDefaults(true);
    try {
      const newBrackets = [];
      for (const [i, def] of DEFAULT_AGE_BRACKETS.entries()) {
        const id = cuid();
        const bracket = {
          id,
          name: def.name,
          minAge: def.minAge,
          maxAge: def.maxAge,
          order: i + 1,
        };
        await createBracket({
          variables: {
            data: { ...bracket, insurancePlan: { id: planId } },
          },
        });
        newBrackets.push(bracket);
      }
      setFieldValue("ageBrackets", [...(ageBrackets || []), ...newBrackets]);
      notification.success({
        message: `${DEFAULT_AGE_BRACKETS.length} tranches d'âge par défaut ajoutées`,
      });
    } catch (err) {
      notification.error({ message: "Échec du chargement par défaut" });
    } finally {
      setLoadingDefaults(false);
    }
  };

  return (
    <Card
      title="Tranches d'âge"
      extra={
        <div className="d-flex" style={{ gap: "8px" }}>
          {(!ageBrackets || ageBrackets.length === 0) && (
            <Button
              size="small"
              onClick={handleLoadDefaults}
              loading={loadingDefaults}
            >
              Par défaut
            </Button>
          )}
          <Button
            type="primary"
            size="small"
            icon={<BiPlus size={14} />}
            onClick={handleAddBracket}
            loading={adding}
          >
            Ajouter
          </Button>
        </div>
      }
    >
      {(!ageBrackets || ageBrackets.length === 0) ? (
        <Empty description="Aucune tranche d'âge définie. Cliquez sur 'Par défaut' ou ajoutez les vôtres." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {ageBrackets.map((bracket, index) => (
            <div
              key={bracket.id}
              className="d-flex"
              style={{ gap: "8px", alignItems: "center" }}
            >
              <Input
                size="small"
                value={bracket.name}
                onChange={(e) => handleLocalChange(index, "name", e.target.value)}
                onBlur={() => handleSaveField(index)}
                style={{ width: 100 }}
                placeholder="Libellé"
              />
              <InputNumber
                size="small"
                min={0}
                value={bracket.minAge}
                onChange={(val) => handleLocalChange(index, "minAge", val)}
                onBlur={() => handleSaveField(index)}
                style={{ width: 80 }}
                placeholder="Min"
                addonBefore="Min"
              />
              <InputNumber
                size="small"
                min={0}
                value={bracket.maxAge}
                onChange={(val) => handleLocalChange(index, "maxAge", val)}
                onBlur={() => handleSaveField(index)}
                style={{ width: 80 }}
                placeholder="Max"
                addonBefore="Max"
              />
              <Button
                type="text"
                danger
                size="small"
                icon={<BiTrash size={14} />}
                onClick={() => handleDeleteBracket(index)}
              />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default AgeBracketManager;
